
import { useEffect } from 'react';
import { Link, useLocation } from '@/lib/router-compat';
import { Github, Linkedin } from '@/components/icons/brand';
import AkivaLogo from '@/components/AkivaLogo';
import { NewsletterSignup } from '@/components/NewsletterSignup';
import { COMPANY_LEGAL_NAME } from '@/lib/company';

// "Subscribe" links target the signup below as /news#newsletter or #newsletter.
// The browser's own fragment jump can't land there: the SPA renders the footer
// after the document loads, the page above keeps growing (the news feed can
// take several seconds), and PageTransition scrolls to the top on route
// changes. So the footer brings the signup into view itself, focuses the
// field, and keeps it in view until the page settles or the visitor scrolls.
const NEWSLETTER_HASH = '#newsletter';
const SETTLE_MS = 12_000;
const VISITOR_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

function revealNewsletterSignup(): () => void {
  const section = document.getElementById('newsletter');
  if (!section) return () => {};
  let settledTop = 0;
  // 'instant': index.css sets scroll-behavior: smooth, and a smooth scroll
  // would still be moving when settledTop is measured.
  const align = () => {
    section.scrollIntoView({ block: 'start', behavior: 'instant' });
    settledTop = section.getBoundingClientRect().top;
  };
  // Every scroll here is programmatic: a visitor's own scroll starts with one
  // of VISITOR_SCROLL_EVENTS, which stops this first.
  const realign = () => {
    if (Math.abs(section.getBoundingClientRect().top - settledTop) > 4) align();
  };
  align();
  document.getElementById('footer-newsletter-email')?.focus({ preventScroll: true });

  const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(realign);
  observer?.observe(document.body);
  window.addEventListener('scroll', realign, { passive: true });
  const stop = () => {
    window.clearTimeout(timer);
    observer?.disconnect();
    window.removeEventListener('scroll', realign);
    VISITOR_SCROLL_EVENTS.forEach((type) => window.removeEventListener(type, stop));
  };
  const timer = window.setTimeout(stop, SETTLE_MS);
  VISITOR_SCROLL_EVENTS.forEach((type) => window.addEventListener(type, stop, { passive: true }));
  return stop;
}

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { hash, key } = useLocation();

  // Re-run on every navigation (key), so a second click on the same link works.
  useEffect(() => (hash === NEWSLETTER_HASH ? revealNewsletterSignup() : undefined), [hash, key]);

  return (
    <footer className="bg-muted/30 text-foreground border-t border-border">
      <div className="container-max py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-8">
          {/* Mission Statement */}
          <div className="md:col-span-4 lg:col-span-2">
            <h3 className="font-semibold text-foreground mb-4">About</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              AI-native engineering firm specializing in enterprise services, domain-specific solutions, and high-performance infrastructure from strategy through deployment. We build innovative systems that drive measurable results for commercial enterprises and government agencies.
            </p>
          </div>
          
          {/* Company */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Company</h3>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors text-sm">About</Link></li>
              <li><Link to="/partnership" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Partnerships</Link></li>
              <li><Link to="/certifications" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Certifications</Link></li>
              {/* /employee/login, not /member/login. Both routes resolve, so
                  this never 404'd: it silently sent employees into the
                  contributor portal. The header's Sign in group points the
                  same door at /employee/login; this matches it. */}
              <li><Link to="/employee/login" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Employees</Link></li>
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Solutions</h3>
            <ul className="space-y-2">
              <li><Link to="/services" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Services</Link></li>
              <li><Link to="/products" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Products</Link></li>
              <li><Link to="/commercial" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Commercial</Link></li>
              <li><Link to="/government" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Government</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Resources</h3>
            <ul className="space-y-2">
              <li><Link to="/contract-vehicles" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Contract Vehicles</Link></li>
              <li><Link to="/careers" className="text-muted-foreground hover:text-foreground transition-colors text-sm">Careers</Link></li>
              <li><Link to="/ai-network" className="text-muted-foreground hover:text-foreground transition-colors text-sm">AI Innovation Network</Link></li>
              <li><Link to="/news" className="text-muted-foreground hover:text-foreground transition-colors text-sm">News</Link></li>
            </ul>
          </div>

          {/* Stay Connected */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Stay Connected</h3>
            <div className="mb-3 flex items-center gap-3 pt-1">
              <a href="https://www.linkedin.com/company/akiva-ai" target="_blank" rel="noopener noreferrer" className="-m-1.5 p-1.5 text-muted-foreground hover:text-primary transition-colors focus-visible:outline-offset-[-4px]" aria-label="LinkedIn">
                <Linkedin className="h-4 w-4" />
              </a>
              <a href="https://github.com/akiva-ai" target="_blank" rel="noopener noreferrer" className="-m-1.5 p-1.5 text-muted-foreground hover:text-primary transition-colors focus-visible:outline-offset-[-4px]" aria-label="Github">
                <Github className="h-4 w-4" />
              </a>
            </div>
            <div id="newsletter" className="scroll-mt-24">
              <p className="mb-2 text-sm leading-6 text-muted-foreground">Insights and industry news</p>
              <NewsletterSignup />
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row justify-between items-center gap-4 border-t border-border pt-8">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="rounded-sm transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/50 focus-visible:ring-offset-0"
              aria-label="Back to top"
            >
              <AkivaLogo className="h-4" />
            </button>
            <span>© {currentYear} {COMPANY_LEGAL_NAME}. All rights reserved.</span>
          </div>
          
          <div className="flex items-center space-x-6 text-sm">
            <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground transition-colors">
              Privacy
            </Link>
            <Link to="/terms-of-service" className="text-muted-foreground hover:text-foreground transition-colors">
              Terms
            </Link>
            <button
              type="button"
              onClick={() => {
                import('@/lib/consent').then(({ clearConsent }) => clearConsent());
              }}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Cookies
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
